import { useNotificationContext } from "@hooks/context/useNotificationContext";
import { useProfileContext } from "@hooks/context/useProfileContext";
import { ProjectContext } from "@hooks/context/useProjectContext";
import {
  NotificationContext,
  useNotification
} from "@hooks/crud/notification/useNotification";
import { CircularProgress, IconButton, Stack } from "@mui/material";
import Avatar from "@ui/atoms/Avatar/Avatar";
import { BaseButton } from "@ui/atoms/BaseButton/BaseButton";
import { BaseDrawerShell } from "@ui/organisms/BaseDrawerShell/BaseDrawerShell";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import { ConfirmationDialog } from "@ui/molecules/ConfirmationDialog/ConfirmationDialog";
import { NotificationViewedIndicator } from "@layout/NotificationDrawer/NotificationDrawer.components";
import { formatRelativeTime } from "@utils/date";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  NotificationLineItem,
  notificationTypeToTitle
} from "catchment-common/interfaces/notification";
import { GridCard } from "@ui/atoms/GridCard/GridCard";

import {
  isNearBottom,
  notificationToRoute
} from "./NotificationDrawer.helpers";
import Icon from "@ui/atoms/Icon/Icon";

// Load more once the user scrolls within this many px of the bottom.
const SCROLL_LOAD_THRESHOLD_PX = 200;

interface NotificationItemProps {
  notification: NotificationLineItem;
  onClear: () => void;
  onNavigate: (notification: NotificationLineItem) => void;
}

/** Hover hook so the card, not the title alone, drives the link affordance. */
const NOTIFICATION_TITLE_CLASS = "notification-item-title";

export const NotificationItem = ({
  notification,
  onClear,
  onNavigate
}: NotificationItemProps) => {
  return (
    <GridCard
      data-test-id="notification-item"
      onClick={() => onNavigate(notification)}
      sx={(theme) => ({
        "&:hover": {
          // The whole card navigates, so the title's underline is driven from
          // the card's hover rather than its own — otherwise only the title
          // looks clickable while every other part of the row navigates too.
          [`& .${NOTIFICATION_TITLE_CLASS}`]: { textDecoration: "underline" },
          boxShadow: `${theme.vars.appEffects.shadow.sm} !important`,
          transform: "none !important"
        },
        p: 1,
        position: "relative"
      })}
    >
      <Stack
        sx={{
          alignItems: "center",
          flexDirection: "row",
          gap: 0.75,
          width: "100%"
        }}
      >
        <NotificationViewedIndicator
          viewed={notification.userViewed ?? false}
        />
        <Avatar
          src={notification.organisationLogo}
          alt={`${notification.organisationName} Logo`}
          size="xxs"
        />

        <Stack
          sx={{
            flex: 1,
            minWidth: 0
          }}
        >
          <Stack
            direction="row"
            sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
          >
            <BaseTypography
              className={NOTIFICATION_TITLE_CLASS}
              variant="body2"
              weight="semiBold"
              text={notificationTypeToTitle(notification.type)}
              sx={{
                color: (theme) => theme.vars.palette.text.secondary
              }}
            />

            <BaseTypography
              variant="body2"
              weight="semiBold"
              sx={{ color: (t) => t.vars.palette.text.secondary }}
              text={formatRelativeTime(notification.created)}
            />
          </Stack>

          <Stack
            direction="row"
            sx={{ alignItems: "flex-end", justifyContent: "space-between" }}
          >
            <Stack>
              <BaseTypography
                variant="caption"
                weight="regular"
                text={notification.organisationName}
                sx={{
                  color: (theme) => theme.vars.palette.text.tertiary
                }}
              />
              <BaseTypography
                variant="caption"
                weight="regular"
                text={notification.projectName}
                sx={{
                  color: (theme) => theme.vars.palette.text.tertiary
                }}
              />
            </Stack>

            <IconButton
              size="small"
              aria-label="clear notification"
              data-test-id="notification-clear"
              onClick={(event) => {
                event.stopPropagation();
                onClear();
              }}
            >
              <Icon name="close" size="sm" />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </GridCard>
  );
};

interface NotificationDrawerProps {
  context?: NotificationContext;
}

export default function NotificationDrawer({
  context
}: NotificationDrawerProps) {
  const navigate = useNavigate();
  const { closeNotificationDrawer, isNotificationDrawerOpen, setUnreadCount } =
    useNotificationContext();
  const { userOrganisation, userProfile } = useProfileContext();

  // If context is explicitly provided, use it. Otherwise determine from available context.
  const projectContextValue = React.useContext(ProjectContext);

  const selectedProject = projectContextValue.selectedProject;

  // If context is provided explicitly, use it. Otherwise:
  // - If we have selectedProject, we're in project view
  // - Otherwise, we're in organisation view
  const notificationContext: NotificationContext =
    context || (selectedProject ? "project" : "organisation");

  // Get IDs for fetching
  const organisationId = userOrganisation?.id;
  const projectId = selectedProject?.id;
  const contractorPackageId = projectContextValue.contractorPackage?.id;

  const {
    clearAllNotifications,
    clearNotification,
    hasMore,
    isLoading,
    loadMore,
    markAsViewed,
    notifications,
    unreadCount: notificationsUnreadCount
  } = useNotification({
    context: notificationContext,
    contractorPackageId,
    organisationId,
    profile: userProfile,
    projectId
  });

  const [openClearDialog, setOpenClearDialog] = useState(false);

  // Update global unread count
  useEffect(() => {
    setUnreadCount(notificationsUnreadCount);
  }, [notificationsUnreadCount, setUnreadCount]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (
      hasMore &&
      !isLoading &&
      isNearBottom(e.currentTarget, SCROLL_LOAD_THRESHOLD_PX)
    ) {
      loadMore();
    }
  };

  const onNavigate = (notification: NotificationLineItem) => {
    const route = notificationToRoute(notification);
    if (route) {
      navigate(route);
    }
    closeNotificationDrawer();
  };

  const handleClearAll = async () => {
    await clearAllNotifications();
    setOpenClearDialog(false);
  };

  const handleClear = async (id: string) => {
    await clearNotification(id);
  };

  const handleOpenClearDialog = () => {
    setOpenClearDialog(true);
  };

  const handleCloseClearDialog = () => {
    setOpenClearDialog(false);
  };

  // Mark notifications as viewed when drawer opens
  useEffect(() => {
    if (isNotificationDrawerOpen && notifications.length > 0) {
      const unviewedIds = notifications
        .filter((n) => !n.userViewed)
        .map((n) => n.id);
      if (unviewedIds.length > 0) {
        markAsViewed(unviewedIds);
      }
    }
  }, [isNotificationDrawerOpen, notifications, markAsViewed]);

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      return b.created.getTime() - a.created.getTime();
    });
  }, [notifications]);

  return (
    <>
      <BaseDrawerShell
        open={isNotificationDrawerOpen}
        onClose={closeNotificationDrawer}
        onContentScroll={handleScroll}
        title="Notifications"
        scrollableContent
        drawerWidth={450}
        actions={
          notifications.length ? (
            <BaseButton
              variant="text"
              text="Clear All Notifications"
              onClick={handleOpenClearDialog}
              sx={{ px: 0 }}
            />
          ) : null
        }
      >
        {sortedNotifications.length > 0 ? (
          <>
            <Stack
              sx={{
                gap: 1
              }}
            >
              {sortedNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClear={() => handleClear(notification.id)}
                  onNavigate={() => onNavigate(notification)}
                />
              ))}
            </Stack>
            {isLoading && (
              <Stack
                sx={{
                  alignItems: "center",
                  py: 2
                }}
              >
                <CircularProgress size={24} />
              </Stack>
            )}
          </>
        ) : null}
      </BaseDrawerShell>
      <ConfirmationDialog
        title="Clear Notifications"
        message="Are you sure you want to clear all notifications?"
        open={openClearDialog}
        onCancel={handleCloseClearDialog}
        onConfirm={handleClearAll}
      />
    </>
  );
}
